import { defineStore } from 'pinia'
import { jwtDecode } from 'jwt-decode'
import { CONFIG } from '@/const/config'
import vuexStore from '@/store'
import { authApi } from '@modules/auth/services/api/authApi'
import { registrationApi } from '@modules/auth/services/api/registrationApi'
import { mapAuthError } from '@modules/auth/services/authErrorMessages'
import { TokenService, type RegistrationStage } from '@modules/auth/services/TokenService'
import type { CountryDto, RoleDto } from '@modules/auth/services/api/types'

const isDevEnv = CONFIG.VUE_APP_MODE === 'DEV' || CONFIG.VUE_APP_MODE === 'development'

// Check if mock mode is active (UI testing without backend)
function isMockMode(): boolean {
  if (!isDevEnv) return false
  const mode = (window as any).__DEV_API_MODE__ || localStorage.getItem('dev_api_mode') || 'real'
  return mode === 'mock'
}

const SENSITIVE_KEYS = ['password', 'token', 'registrationToken', 'accessToken', 'code', 'otpId']
const PARTIAL_MASK_KEYS = ['phone', 'email', 'dateOfBirth', 'firstName', 'lastName', 'surName', 'secondName']

function maskValue(val: string): string {
  if (!val || val.length <= 2) return '****'
  return val.slice(0, Math.min(4, Math.ceil(val.length / 3))) + '****'
}

function maskData(data: any, depth = 0): any {
  if (depth > 5) return '...'
  if (data === null || data === undefined) return data
  if (typeof data === 'string') return data
  if (Array.isArray(data)) return data.map(item => maskData(item, depth + 1))
  if (typeof data === 'object') {
    const masked: Record<string, any> = {}
    for (const key of Object.keys(data)) {
      const lk = key.toLowerCase()
      if (SENSITIVE_KEYS.some(sk => lk.includes(sk.toLowerCase()))) {
        masked[key] = '********'
      } else if (PARTIAL_MASK_KEYS.some(pk => lk === pk.toLowerCase())) {
        masked[key] = typeof data[key] === 'string' ? maskValue(data[key]) : data[key]
      } else {
        masked[key] = maskData(data[key], depth + 1)
      }
    }
    return masked
  }
  return data
}

function logRequest(label: string, data: any) {
  console.log(`[Registration] ${label}:`, JSON.stringify(maskData(data), null, 2))
}

function logResponse(label: string, status: number, data: any) {
  console.log(`[Registration] ${label} response (${status}):`, JSON.stringify(maskData(data), null, 2))
}

const DEFAULT_ROLES: RoleDto[] = [
  { id: 1, name: 'Owner' },
  { id: 3, name: 'Director' },
  { id: 4, name: 'UBO' },
  { id: 6, name: 'Authorized Person' },
]

interface RegistrationState {
  loading: boolean
  error: string | null
  backendErrors: Record<string, string[]>
  permissions: string[] | null
  otpId: string | null
  testCode: string | null
  phone: string
  currentStep: string
  countryList: CountryDto[]
  countryDocs: unknown
  roleList: RoleDto[]
  step3Data: Record<string, any> | null
  personalData: Record<string, any> | null
  currentCompany: Record<string, any> | null
}

export const useRegistrationStore = defineStore('registration', {
  state: (): RegistrationState => ({
    loading: false,
    error: null,
    backendErrors: {},
    permissions: null,
    otpId: null,
    testCode: null,
    phone: '',
    currentStep: '1',
    countryList: [],
    countryDocs: null,
    roleList: [],
    personalData: null,
    step3Data: TokenService.getStep3Data(),
    currentCompany: null,
  }),

  actions: {
    setStep(step: string) { this.currentStep = step },
    setPhone(phone: string) { this.phone = phone },
    setError(error: string | null) { this.error = error },

    setStep3Data(data: Record<string, any>) {
      this.step3Data = data
      TokenService.setStep3Data(data)
    },

    clearStepData(step?: string) {
      this.error = null
      this.backendErrors = {}
      if (!step || step === '1') {
        this.otpId = null
        this.testCode = null
        this.phone = ''
        TokenService.clearRegistrationTokens()
      }
      if (!step || step === '3') {
        this.step3Data = null
        TokenService.clearStep3Data()
      }
    },

    /**
     * Stage-aware registration token setter (delegates to TokenService).
     * Kept on the store because some components (e.g. Step5CompanyStructure)
     * call it directly via the store reference.
     */
    setRegistrationToken(token: string, step: RegistrationStage = 1) {
      TokenService.setRegistrationToken(token, step)
    },

    async initOtp(payload: { phone: string; validateMethod: string; deliveryMethod: string }) {
      this.loading = true
      this.error = null
      logRequest('Step 1 - initOtp', payload)
      try {
        const res = await authApi.initOtp(payload)
        logResponse('Step 1 - initOtp', res.status, res.data)
        if (res.data?.otpId) {
          this.otpId = res.data.otpId
        }
        if (res.data?.testCode) {
          this.testCode = res.data.testCode
          console.log('[Registration] OTP test code from backend:', res.data.testCode)
        }
        return res
      } catch (e: any) {
        this.error = mapAuthError(e, 'Failed to send verification code')
        throw e
      } finally {
        this.loading = false
      }
    },

    async verifyOtp(code: string) {
      this.loading = true
      this.error = null
      logRequest('Step 1 - verifyOtp', { otpId: this.otpId, code })
      try {
        const res = await authApi.verifyOtp({ otpId: this.otpId ?? '', code })
        logResponse('Step 1 - verifyOtp', res.status, res.data)
        if (res.status === 200 && res.data.registrationToken) {
          TokenService.logout()
          TokenService.setRegistrationToken(res.data.registrationToken, 1)
          const decoded = jwtDecode<{ UserId?: string }>(res.data.registrationToken)
          if (decoded?.UserId) TokenService.setUserId(decoded.UserId)
          this.permissions = (res.data.permissions ?? null) as string[] | null
        }
        return res
      } catch (e: any) {
        const code = e.response?.data?.code
        if (code === 'OtpNotValid') {
          this.error = 'Code is wrong. Try again.'
        } else if (code === 'OtpExpired') {
          this.error = e.response?.data?.message || 'Code has expired'
        } else {
          this.error = mapAuthError(e, 'Verification failed')
        }
        throw e
      } finally {
        this.loading = false
      }
    },

    async createNewEntity() {
      // Idempotency guard: do not create another entity if one already exists
      const existing = TokenService.getEntityId()
      if (existing) {
        console.log('[Registration] Step 2 - createNewEntity: entity already exists', existing)
        return { status: 200, data: { entityId: existing } }
      }
      this.loading = true
      logRequest('Step 2 - createNewEntity', {})
      try {
        const { status, data } = await authApi.createEntity()
        logResponse('Step 2 - createNewEntity', status, data)
        if (status === 200 && data?.entityId) {
          TokenService.setEntityId(data.entityId)
        }
        return { status, data }
      } catch (e: any) {
        const code = e.response?.data?.code
        if (code === 'LegalUserDoesNotHavePassword') {
          this.error = 'Please set a password before proceeding.'
        } else {
          this.error = mapAuthError(e, 'Failed to create entity')
        }
        throw e
      } finally {
        this.loading = false
      }
    },

    async submitPersonalInfo(payload: Record<string, any>) {
      this.loading = true
      this.error = null
      this.backendErrors = {}
      logRequest('Step 2 - submitPersonalInfo', payload)
      if (isMockMode()) {
        await new Promise(r => setTimeout(r, 500))
        const devToken = 'dev-stage2-token-' + Date.now()
        TokenService.setRegistrationToken(devToken, 2)
        this.loading = false
        console.log('[Registration] DEV MODE: submitPersonalInfo bypassed')
        return { status: 200, data: { token: devToken } }
      }
      try {
        const token = TokenService.getRegistrationToken()
        const res = await registrationApi.submitPersonalInfo(payload, token)
        logResponse('Step 2 - submitPersonalInfo', res.status, res.data)
        if (res.data?.token) {
          TokenService.setRegistrationToken(res.data.token, 2)
        }
        // Auto-create entity after successful personal info submission
        await this.createNewEntity()
        return res
      } catch (e: any) {
        console.error('[Registration] Step 2 - submitPersonalInfo error:', e.response?.status, maskData(e.response?.data))
        if (e.response?.status === 422 && e.response?.data) {
          this.backendErrors = e.response.data
        }
        this.error = mapAuthError(e, 'Failed to submit personal info')
        throw e
      } finally {
        this.loading = false
      }
    },

    async getCountryList() {
      this.loading = true
      if (isMockMode()) {
        this.countryList = [
          { code: 'GB', name: 'United Kingdom' },
          { code: 'ES', name: 'Spain' },
          { code: 'DE', name: 'Germany' },
          { code: 'FR', name: 'France' },
          { code: 'IT', name: 'Italy' },
          { code: 'NL', name: 'Netherlands' },
          { code: 'US', name: 'United States' },
          { code: 'LT', name: 'Lithuania' },
          { code: 'EE', name: 'Estonia' },
        ].sort((a, b) => a.name.localeCompare(b.name))
        this.loading = false
        return this.countryList
      }
      try {
        const { data, status } = await registrationApi.getCountries()
        if (status === 200 && data) {
          this.countryList = data.map((c) => {
            if (c.name.toLowerCase().includes('united kingdom')) {
              return { ...c, name: 'United Kingdom' }
            }
            return c
          }).sort((a, b) =>
            a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase())
          )
        }
        return this.countryList
      } catch (e: any) {
        this.error = mapAuthError(e, 'Failed to fetch countries')
        throw e
      } finally {
        this.loading = false
      }
    },

    async getCountryDocsByCode(code: string) {
      try {
        const { data } = await registrationApi.getCountryDocs(code)
        this.countryDocs = data
        return data
      } catch {
        // Non-critical — don't set error or loading state
        return null
      }
    },

    async getRoleList() {
      this.loading = true
      try {
        const { data } = await registrationApi.getRoles()
        this.roleList = data && data.length ? data : DEFAULT_ROLES
        return this.roleList
      } catch {
        // Fallback to default roles if API fails
        this.roleList = DEFAULT_ROLES
        return this.roleList
      } finally {
        this.loading = false
      }
    },

    async createCompanyInfo(payload: Record<string, any>) {
      this.loading = true
      this.error = null
      this.backendErrors = {}
      logRequest('Step 4 - createCompanyInfo', payload)
      if (isMockMode()) {
        await new Promise(r => setTimeout(r, 500))
        const devToken = 'dev-stage3-token-' + Date.now()
        TokenService.setRegistrationToken(devToken, 5)
        this.loading = false
        console.log('[Registration] DEV MODE: createCompanyInfo bypassed')
        return { status: 200, data: { token: devToken } }
      }
      try {
        const res = await registrationApi.createCompanyInfo(payload)
        logResponse('Step 4 - createCompanyInfo', res.status, res.data)
        if (res.data?.token) {
          TokenService.setRegistrationToken(res.data.token, 5)
        } else if (res.data?.legalEntityId) {
          TokenService.setRegistrationToken(res.data.legalEntityId, 5)
        }
        return res
      } catch (e: any) {
        console.error('[Registration] Step 4 - createCompanyInfo error:', e.response?.status, maskData(e.response?.data))
        if (e.response?.status === 422 && e.response?.data) {
          this.backendErrors = e.response.data
        }
        this.error = mapAuthError(e, 'Failed to submit company info')
        throw e
      } finally {
        this.loading = false
      }
    },

    async createCompanyStructure(payload: Record<string, any>) {
      this.loading = true
      this.error = null
      this.backendErrors = {}
      logRequest('createCompanyStructure', payload)
      if (isMockMode()) {
        await new Promise(r => setTimeout(r, 500))
        TokenService.setRegistrationToken('dev-stage4-completed', 6)
        this.loading = false
        console.log('[Registration] DEV MODE: createCompanyStructure bypassed')
        return { status: 200, data: {} }
      }
      try {
        const res = await registrationApi.createCompanyStructure(payload)
        logResponse('createCompanyStructure', res.status, res.data)
        TokenService.setRegistrationToken('completed', 6)
        return res
      } catch (e: any) {
        console.error('[Registration] createCompanyStructure error:', e.response?.status, maskData(e.response?.data))
        if (e.response?.status === 422 && e.response?.data) {
          this.backendErrors = e.response.data
        }
        this.error = mapAuthError(e, 'Failed to submit company structure')
        throw e
      } finally {
        this.loading = false
      }
    },

    async loginCurrentCompany(data: { companyId: string }) {
      this.loading = true
      this.error = null
      logRequest('Step 5 - loginCurrentCompany', data)
      try {
        const res = await registrationApi.loginCurrentCompany(data)
        logResponse('Step 5 - loginCurrentCompany', res.status, res.data)
        TokenService.setEntityId(data.companyId)
        TokenService.setAccessToken(res.data.accessToken)
        if (res.data?.permissions) {
          TokenService.setPermissions(res.data.permissions)
          // Sync permissions into Vuex so portal-right-gated fetches
          // (accounts, currencies, cards) fire on first dashboard load.
          vuexStore.commit('SET_PERMISSIONS', res.data.permissions)
        }
        return res
      } catch (e: any) {
        this.error = mapAuthError(e, 'Failed to select company', { unauthorizedAs: 'session-expired' })
        throw e
      } finally {
        this.loading = false
      }
    },

    async getCompanyById(id: string) {
      if (!id) return null
      this.loading = true
      logRequest('Step 5 - getCompanyById', { id })
      try {
        const { data } = await registrationApi.getCompanyById(id)
        logResponse('Step 5 - getCompanyById', 200, data)
        this.currentCompany = data
        return data
      } catch (e: any) {
        this.error = mapAuthError(e, 'Failed to fetch company')
        throw e
      } finally {
        this.loading = false
      }
    },

    /**
     * Highest registration step the user can resume to. Delegates to
     * TokenService to keep cookie key knowledge in one place.
     */
    getHighestValidStep(): number {
      return TokenService.getHighestValidRegistrationStep()
    },

    async getInvoice() {
      console.log('[Registration] Step 5 - getInvoice: GET /api/payments/invoice')
      const result = await registrationApi.getInvoice()
      console.log('[Registration] Step 5 - getInvoice response:', result.status, result.headers['content-type'])
      return result
    },

    async paidInvoice(entityId: string) {
      this.loading = true
      this.error = null
      logRequest('Step 5 - paidInvoice', { entityId })
      try {
        const res = await registrationApi.paidInvoice(entityId)
        logResponse('Step 5 - paidInvoice', res.status, res.data)
        return res
      } catch (e: any) {
        console.error('[Registration] Step 5 - paidInvoice error:', e.response?.status, maskData(e.response?.data))
        this.error = mapAuthError(e, 'Failed to mark invoice as paid')
        throw e
      } finally {
        this.loading = false
      }
    },

    async sendInvoiceEmail(email: string) {
      this.loading = true
      this.error = null
      logRequest('Step 5 - sendInvoiceEmail', { email })
      try {
        const res = await registrationApi.sendInvoiceEmail(email)
        logResponse('Step 5 - sendInvoiceEmail', res.status, res.data)
        return res
      } catch (e: any) {
        console.error('[Registration] Step 5 - sendInvoiceEmail error:', e.response?.status, maskData(e.response?.data))
        this.error = mapAuthError(e, 'Failed to send invoice')
        throw e
      } finally {
        this.loading = false
      }
    },
  },
})
